import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import type { AnalyticsSession } from "@/lib/analyticsDB";

const CHALLENGE_LABELS: Record<string, string> = {
  staff: "Staff management",
  operations: "Operations tracking",
  reporting: "Reporting & visibility",
  billing: "Billing & revenue",
  performance: "Performance management",
  marketing: "Marketing & lead generation",
  sales: "Sales pipeline & CRM",
  workplace: "Workplace services",
};

const COLORS = ["#10b981", "#059669", "#34d399", "#14b8a6", "#0ea5e9", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899", "#84cc16"];

function collectChallengeCounts(sessions: AnalyticsSession[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const s of sessions) {
    const val = s.answers?.challenges;
    if (Array.isArray(val)) {
      for (const v of val) {
        if (typeof v === "string") {
          counts.set(v, (counts.get(v) || 0) + 1);
        }
      }
    } else if (typeof val === "string") {
      counts.set(val, (counts.get(val) || 0) + 1);
    }
  }
  return counts;
}

export default function ChallengeDistribution({ sessions }: { sessions: AnalyticsSession[] }) {
  const counts = collectChallengeCounts(sessions);

  const data = Array.from(counts.entries())
    .map(([key, count]) => ({
      key,
      label: CHALLENGE_LABELS[key] || key,
      count,
    }))
    .sort((a, b) => b.count - a.count);

  if (data.length === 0) {
    return (
      <div className="glass-card p-4 md:p-5">
        <h3 className="text-base font-bold text-white mb-1">Top User Problems</h3>
        <p className="text-xs text-silver-400 mb-4">What users say is their biggest challenge</p>
        <div className="py-12 text-center text-sm text-silver-400">
          No challenge data available yet.
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-4 md:p-5">
      <div className="mb-5">
        <h3 className="text-base font-bold text-white">Top User Problems</h3>
        <p className="text-xs text-silver-400 mt-0.5">What users say is their biggest challenge</p>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="w-full sm:w-1/2" style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={45}
                stroke="#161622"
                strokeWidth={2}
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#161622",
                  border: "1px solid #2e2e3e",
                  borderRadius: 8,
                  color: "#e2e2ea",
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => [`${value} users`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="w-full sm:w-1/2 space-y-2.5">
          {data.map((d, i) => (
            <div key={d.key} className="flex items-center gap-2.5">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="text-xs text-silver-200 flex-1">{d.label}</span>
              <span className="text-xs font-medium text-white">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}